// app-record-v2.type.ts

import { APP_TYPES, ROLE_SIGNATURE } from "@lhd/components/CloneApp/constants";
import { AppPartType } from "@lhd/features/Apps/AppForm/types";

/* =========================
 * Value
 * ========================= */
export interface AppRecordValueV2 {
  id: number;
  app_part_id: number;
  value: any; // ISO date, text, number (stringified)
  background_color: string | null;
  text_color: string | null;
  valueFormat?: string | any[];
}

/* =========================
 * Attachment (future-proof)
 * ========================= */
export interface AppRecordAttachment {
  id?: number;
  name?: string;
  url?: string;
  size?: number;
  mime_type?: string;
}

/* =========================
 * Record Type
 * ========================= */
export type AppRecordType = "owner" | "child" | "reference" | string;

export interface RowDataTable {
  row_id: number;
  row_order: number;
  cells: AppRecordValueV2[];
  client_id: string;
}
export interface TableData {
  table_id: number;
  table_name: string;
  field_code: string;
  app_part_type: string;
  rows: RowDataTable[];
}
/* =========================
 * Main Record V2
 * ========================= */

export enum CONTRACT_STATUS {
  DRAFTING = "drafting",
  PENDING_SIGNATURE = "pending_signature",
  ACTIVE = "active",
  ARCHIVED = "archived",
}

export const CONTRACT_STATUS_LABEL: Record<string, string> = {
  [CONTRACT_STATUS.DRAFTING]: "作成中",
  [CONTRACT_STATUS.PENDING_SIGNATURE]: "契約待ち",
  [CONTRACT_STATUS.ACTIVE]: "適用中",
  [CONTRACT_STATUS.ARCHIVED]: "過去ログ",
};

export enum SignerStatus {
  PENDING = "pending",
  SIGNED = "signed",
}

interface Signer {
  worker_id: number;
  user_id: number | null;
  status: SignerStatus;
  signed_at: string | null;
  signer_name: string;
  signature_image_url: string | null;
}

interface ContractPartSignature {
  part_id: number;
  part_name: string;
  role: ROLE_SIGNATURE;
  is_signed: boolean;
  required_signer_count: number;
  signed_signer_count: number;
  signed_at: string | null;
  signer_name: string | null;
  signature_image_url: string | null;
  signers: Signer[];
}

export interface AppRecordV2 {
  id: number;
  app_id: number;
  user_id: number;
  user_update_id: number;

  row_id: number;
  parent_record_id: number | null;
  record_type: AppRecordType;

  created_at: string; // ISO datetime
  updated_at: string; // ISO datetime
  last_update_at: string;
  sort_order: number;
  regular_values: AppRecordValueV2[];
  attachments: AppRecordAttachment[];

  is_used_referenced_data: boolean;
  clone_value_hash: string | null;
  share_value_hash: string[];
  table_data: TableData[];

  contract_info: {
    contract_status: CONTRACT_STATUS;
    signatures: ContractPartSignature[];
  };
  policy_permissions?: {
    record_read: boolean;
    record_edit: boolean;
    record_delete: boolean;
  };
}

export interface PermissionV2 {
  view: boolean;
  edit: boolean;
  delete: boolean;
}

export interface CompanyPermissions {
  company_view: string[];
  company_edit: string[];
  company_delete: string[];
}
export type AppTypes = typeof APP_TYPES[keyof typeof APP_TYPES];

export interface AppV2 {
  id: number;
  name: string;
  description: string;
  hex_color: string;
  app_type: AppTypes;
  picture: string;
  app_parts: AppPartType[];
  company_id: number;
  company_permissions: CompanyPermissions;
  copy_from_app_id: number | null;
  copy_type: string | null;
  parent_id: number | null;
  sort_index: number;
  store_status: "approved" | "pending" | "rejected" | string;
  total_parts_count: number;
  total_records_count: number;
  last_record_created_at: string | null;
  user_id: number;
  created_at: string; // ISO datetime
  updated_at: string; // ISO datetime
}

export interface IPermissionItem {
  view?: boolean;
  edit?: boolean;
  delete?: boolean;
  create?: boolean;
  all?: boolean;
}
export type CompanyRecordPermission = {
  record_read: boolean;
  record_edit: boolean;
  record_delete: boolean;
};

export interface DuplicateTargetCompanyIdsResponse {
  results: { record_id: number; company_ids: number[] }[];
}

export const FALLBACK_PERMISSION: IPermissionItem = {
  create: true,
  edit: true,
  delete: true,
  view: true,
  all: true,
};
